import { ILogin } from "@/interfaces/login.interface";
import { IUsuario } from "@/interfaces/usuarios.interface";
import { canAccessIgrejaMode, getEventoPermissoes } from "@/lib/evento-permissoes";
import { hasIgrejaArea, isIgrejaAdm, type IgrejaArea } from "@/lib/igreja-permissoes";
import { arrayUniqueByKey } from "@/lib/utils";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { usersIcon } from "@/components/leafet-markers/marker";
import { setUserData, clearUserData } from "@/lib/gtm";
import { isStandalonePWA } from "@/utils/url-utils";

/**
 * Estado minimo usado pelos selectors deste slice.
 * NAO importar RootState de ../store — isso cria ciclo auth ↔ store e TDZ em authReducer.
 */
type AuthSelectorState = {
  authReducer: IUsuario;
  generalReducer: { LoginId: number | null };
  eventosReducer?: {
    Eventos?: Array<{
      Id?: number;
      ConfiguracaoId?: number | null;
      logins?: { usuarioLogins?: unknown[] };
      configuracaos?: unknown;
      usuarioEventos?: Array<{ permissao?: string } | null> | null;
      equipanteeventos?: Array<{ Tipo?: number; padrinhos?: unknown } | null> | null;
      circulos?: unknown[] | null;
    }> | null;
    EventoId?: number | null;
  };
};

function resolveSessionKindFromPayload(payload: IUsuario): IUsuario["session_kind"] {
  if (payload.session_kind === "pwa" || payload.session_kind === "browser") {
    return payload.session_kind;
  }
  return isStandalonePWA() ? "pwa" : "browser";
}

const emptyAuthState: IUsuario = {
  name: "",
  email: "",
  access_token: "",
  refresh_token: "",
  session_kind: undefined,
  fotoId: null,
  id: "",
  logins: null,
  foto: null,
  usuarioEventos: null,
  usuarioLogins: null,
  usuarioConfiguracaos: null,
  Master: null,
  CriadoViaTrial: null,
  Mercadopago: null,
};

function applyUserPayload(state: IUsuario, payload: IUsuario) {
  state.name = payload.name;
  state.email = payload.email;
  state.access_token = payload.access_token;
  state.refresh_token = payload.refresh_token;
  state.session_kind = resolveSessionKindFromPayload(payload);
  state.fotoId = payload.fotoId;
  state.id = payload.id;
  state.logins =
    payload.logins?.length > 0
      ? payload.logins
      : (arrayUniqueByKey(
          payload.usuarioEventos.map((uv) => uv.eventosview.logins),
          "Id",
        ) as any[]);
  state.foto = payload.foto;
  state.usuarioConfiguracaos = payload.usuarioConfiguracaos;
  state.usuarioEventos = payload.usuarioEventos;
  state.usuarioLogins = payload.usuarioLogins;
  state.Master = payload.Master;
  state.CriadoViaTrial = payload.CriadoViaTrial ?? null;
  state.Mercadopago = payload.Mercadopago ?? null;
}

/** Hidrata Redux antes do primeiro render para evitar 401 em abas novas. */
function loadInitialAuthState(): IUsuario {
  if (typeof window === "undefined") return { ...emptyAuthState };
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return { ...emptyAuthState };
    const parsed = JSON.parse(raw) as IUsuario;
    if (!parsed?.access_token || !parsed?.refresh_token) return { ...emptyAuthState };
    const state = { ...emptyAuthState, ...parsed };
    state.session_kind = resolveSessionKindFromPayload(parsed);
    return state;
  } catch {
    return { ...emptyAuthState };
  }
}

export const authSlice = createSlice({
  name: "authSlice",
  initialState: loadInitialAuthState(),
  reducers: {
    UpdateLogin: (state: IUsuario, { payload }: PayloadAction<any>) => {
      state.logins = [
        ...state.logins.map((l) => {
          if (l.Id === payload.Id) {
            return payload;
          } else return l;
        }),
      ];

      localStorage.setItem("user", JSON.stringify(state));
    },
    AddLogin: (state: IUsuario, { payload }: PayloadAction<any>) => {
      if (!payload?.Id || state.logins?.some((l) => l.Id === payload.Id)) return;
      state.logins = [...(state.logins || []), payload];
      localStorage.setItem("user", JSON.stringify(state));
    },
    setEquipante: (state: IUsuario, { payload }: PayloadAction<any>) => {
      state.equipantes = payload;
    },
    setAuthenticatedUser: (
      state: IUsuario,
      { payload }: PayloadAction<IUsuario>
    ) => {
      applyUserPayload(state, payload);
      localStorage.setItem("user", JSON.stringify(state));

      const loginIdStorage = localStorage.getItem("LoginId");
      const loginId = loginIdStorage ? Number(loginIdStorage) : null;
      setUserData(payload, loginId);
    },
    /** Atualiza Redux a partir de outra aba sem regravar localStorage. */
    syncAuthenticatedUserFromPeer: (
      state: IUsuario,
      { payload }: PayloadAction<IUsuario>
    ) => {
      applyUserPayload(state, payload);
    },
    /** Limpa Redux sem tocar localStorage (outra aba já removeu a sessão). */
    clearAuthStateOnly: (state: IUsuario) => {
      state.access_token = "";
      state.refresh_token = "";
      state.session_kind = undefined;
      state.name = "";
      state.email = "";
      state.fotoId = null;
      state.id = "";
      state.logins = [];
      state.Master = null;
      state.CriadoViaTrial = null;
      state.Mercadopago = null;
      state.usuarioConfiguracaos = null;
      state.usuarioEventos = null;
      state.usuarioLogins = null;
      state.foto = null;
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("mode-selection-shown");
      }
      clearUserData();
    },
    resetState: (state: IUsuario) => {
      state.access_token = "";
      state.refresh_token = "";
      state.session_kind = undefined;
      state.name = "";
      state.email = "";
      state.fotoId = null;
      state.id = "";
      state.logins = [];
      state.Master = null;
      state.CriadoViaTrial = null;
      state.Mercadopago = null;
      state.usuarioConfiguracaos = null;
      state.usuarioEventos = null;
      state.usuarioLogins = null;
      state.foto = null;
      localStorage.removeItem("user");
      // Limpar flag de seleção de modo para que o modal apareça no próximo login
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("mode-selection-shown");
      }

      // Limpar dados do usuário no GTM dataLayer
      clearUserData();
    },
  },
});

export const {
  setAuthenticatedUser,
  syncAuthenticatedUserFromPeer,
  clearAuthStateOnly,
  resetState,
  UpdateLogin,
  AddLogin,
  setEquipante,
} = authSlice.actions;
export const authReducer = authSlice.reducer;
export const selectAuthenticatedUser = (state: AuthSelectorState) => state.authReducer;

const getLogins = createSelector(
  [(state: AuthSelectorState) => state.authReducer],
  (state: IUsuario) => state.logins
);

const Login = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const loginId = Number(state.generalReducer.LoginId);
    if (!loginId || Number.isNaN(loginId)) return undefined;
    return getLogins(state)?.find((x) => x.Id === loginId);
  }
);
const PermissoesSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    // Inline (nao importar eventos.slice) para evitar ciclo:
    // auth → eventos → general → auth → TDZ em authReducer.
    const eventosReducer = state.eventosReducer;
    const SelectedEvento = eventosReducer?.Eventos?.find(
      (ev) => ev.Id == eventosReducer.EventoId,
    );
    const User = state.authReducer;
    return getEventoPermissoes(User, SelectedEvento);
  }
);

const isOrgAdmSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);

    if (!User || !selectedOrg) return false;

    const hasLogins = User.logins?.some((o) => {
      return o.Id === selectedOrg?.Id;
    });
    const hasUsuarioLogins = User.usuarioLogins?.some((o) => {
      return o.logins?.Id === selectedOrg?.Id;
    });
    const result = hasLogins || hasUsuarioLogins;
    return result;
  }
);

const isEnsinoAdm = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);

    if (!User || !selectedOrg) return false;

    // Verifica se o usuário tem permissão ENSINO para o login selecionado
    const result = User.usuarioLogins?.some((o) => {
      return (
        o.tipoPermissao === "ENSINO" &&
        o.logins?.Id === selectedOrg?.Id
      );
    });

    return result || false;
  }
);

const isInfantilAdm = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);

    if (!User || !selectedOrg) return false;

    return (
      User.usuarioLogins?.some(
        (o) =>
          o.tipoPermissao === "INFANTIL" && o.logins?.Id === selectedOrg?.Id,
      ) || false
    );
  },
);

const canAccessIgrejaModeSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);
    return canAccessIgrejaMode(User, selectedOrg?.Id);
  }
);

const isIgrejaAdmSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);
    return isIgrejaAdm(User, selectedOrg?.Id);
  }
);

export function selectHasIgrejaArea(area: IgrejaArea) {
  return createSelector(
    [(state: AuthSelectorState) => state],
    (state: AuthSelectorState) => {
      const User = state.authReducer;
      const selectedOrg = Login(state);
      return hasIgrejaArea(User, selectedOrg?.Id, area);
    },
  );
}

const tipoEventosAdmSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);

    if (!User || !selectedOrg) return [];

    return User?.Master
      ? [
        User.logins
          .filter((o) => o.Id === selectedOrg?.Id)
          .map((o) => o.configuracaos)[0],
      ]
      : User.usuarioConfiguracaos?.map((o) => o.configuracaos) || [];
  }
);

// Selector para verificar acesso total à agenda (Master, ESCALAS ou IGREJA)
const hasAgendaAcessoTotalSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);

    if (!User || !selectedOrg) return false;
    if (User.Master) return true;
    return (
      User.usuarioLogins?.some(
        (l: any) =>
          (l.logins?.Id === selectedOrg?.Id || l.loginsId === selectedOrg?.Id) &&
          (l.tipoPermissao === "ESCALAS" ||
            l.tipoPermissao === "IGREJA" ||
            l.tipoPermissao === "IGREJA_AGENDA")
      ) || false
    );
  }
);

// Novo selector para verificar se o usuário tem permissão de vendas em algum evento
const hasVendasPermissionSelector = createSelector(
  [(state: AuthSelectorState) => state],
  (state: AuthSelectorState) => {
    const User = state.authReducer;
    const selectedOrg = Login(state);

    if (!User || !selectedOrg) return false;

    // Se o usuário é Master, tem permissão de vendas
    if (User.Master) return true;

    // Verificar se o usuário tem permissão de vendas em algum evento
    if (User.usuarioEventos?.some((evento) => evento.permissao === "Vendas")) {
      return true;
    }

    // Verificar se o usuário tem permissão de vendas em alguma configuração
    if (
      User.usuarioConfiguracaos?.some((config) => config.permissao === "Vendas")
    ) {
      return true;
    }

    if (hasIgrejaArea(User, selectedOrg.Id, "IGREJA_VENDAS")) {
      return true;
    }

    return false;
  }
);

export const authSelectors = {
  Login,
  getLogins,
  PermissoesSelector,
  isOrgAdmSelector,
  isEnsinoAdm,
  isInfantilAdm,
  tipoEventosAdmSelector,
  canAccessIgrejaModeSelector,
  isIgrejaAdmSelector,
  hasVendasPermissionSelector,
  hasAgendaAcessoTotalSelector,
};
